import { Box, DialogActions, DialogContent, Theme } from '@mui/material'
import { GridRenderEditCellParams, GridTreeNodeWithRender } from '@mui/x-data-grid-premium'
import { useFormikContext } from 'formik'
import { useMemo } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { cn } from '~/@/lib/utils'
import { DataType } from '~/common/generated/apollo-operations'
import { useDeviceType } from '~/common/hooks/useDeviceQuery'
import { Prompt } from '~/common/hooks/usePrompt'
import useStyles from '~/common/hooks/useStyles'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { localKeyMap } from '~/common/utils/keymap'
import { RecordCellDetails } from '~/components/records/record-cell-details/RecordCellDetails'
import { checkFormikDirtyState, getTableCellInitialValues } from '../../common/utils/formik'
import { RecordDetailsCellPermissionsProvider } from '../records/RecordDetailsCellPermissionsProvider'
import { DetailedTableColumn, DossTableCell, DossTableRow } from '../tables/types'
import { EditTableCellFormButtons } from './EditTableCellFormButtons'
import {
  EditableTableCellOriginProvider,
  RecordCellEditOrigin,
} from './useEditableTableCellOriginProvider'

function getStyles(theme: Theme) {
  return {
    content(minWidth: number, isMobileLookupTableCellEdit: boolean) {
      return {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: theme.spacing(0),
        minWidth: isMobileLookupTableCellEdit ? '100%' : minWidth,
        ...(isMobileLookupTableCellEdit && {
          overflow: 'hidden',
        }),
      }
    },
    buttons: {
      display: 'flex',
      paddingTop: theme.spacing(1),
      paddingInlineEnd: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      paddingInlineStart: theme.spacing(2),
    },
  }
}

interface EditTableCellFormProps {
  cell: DossTableCell
  row: DossTableRow
  maxColWidth: number
  loading: boolean
  params: GridRenderEditCellParams<
    DossTableRow,
    DossTableCell,
    DetailedTableColumn,
    GridTreeNodeWithRender
  >
  setIsLoading: (loading: boolean) => void
  success: boolean
  setIsSuccess: (loading: boolean) => void
  failure: boolean
  setIsFailure: (loading: boolean) => void
  handleCancel: () => Promise<void>
  initialColWidth: number
  addDocumentId: (documentId: string) => void
  removeDocumentId: (documentId: string) => void
  value: string[] | string | boolean | number | null
  tableId: string
}

/**
 * Renders a form component for editing a table cell
 */
export function EditTableCellForm({
  cell,
  row,
  maxColWidth,
  loading,
  params,
  setIsLoading,
  success,
  setIsSuccess,
  failure,
  setIsFailure,
  handleCancel,
  initialColWidth,
  addDocumentId,
  removeDocumentId,
  value,
  tableId,
}: EditTableCellFormProps) {
  const { t } = useTypedTranslation()
  const formikProps = useFormikContext<Record<string, unknown>>()
  const { isMobile } = useDeviceType()
  const styles = useStyles(getStyles)
  // Derive the rows already referenced by this cell's lookup-column-group
  // siblings directly from `row.cells` — every detailed cell already includes
  // `lookupCells { row { id } }`, so we don't need a separate fetch. Same data
  // the `tableCellGroup` GraphQL query used to return.
  const lookupColumnGroupId = cell.column.lookupColumnGroup?.id
  const tableCellGroupLookupRowIds = useMemo(() => {
    if (!lookupColumnGroupId) {
      return []
    }
    const ids = row.cells
      .filter((groupCell) => groupCell.column.lookupColumnGroup?.id === lookupColumnGroupId)
      .flatMap((groupCell) => groupCell.lookupCells.map((lookupCell) => lookupCell.row.id))
    return Array.from(new Set(ids))
  }, [lookupColumnGroupId, row.cells])

  const ref = useHotkeys(
    [localKeyMap.CONTROL_ENTER, localKeyMap.COMMAND_ENTER],
    (event) => {
      event.preventDefault()
      formikProps.handleSubmit()
      event.stopPropagation()
    },
    { enableOnFormTags: true },
  )

  const columnWidth = useMemo(() => {
    switch (cell.column.type) {
      case DataType.ID:
        return maxColWidth !== initialColWidth ? initialColWidth + 136 : maxColWidth + 136
      case DataType.NUMBER:
      case DataType.STRING:
      case DataType.BOOLEAN:
      case DataType.FORMULA:
      case DataType.FORMULA_NUMBER:
      case DataType.FORMULA_STRING:
        return maxColWidth !== initialColWidth ? initialColWidth + 8 : maxColWidth + 8
      case DataType.FORMULA_BOOLEAN:
        // FORMULA_BOOLEAN is out of scope for the non-numeric formulas project
        return undefined
      case DataType.ARRAY:
        if (cell.column.innerType === DataType.ENUM || cell.column.innerType === DataType.USER) {
          return 0
        }
        return maxColWidth + 72
      case DataType.LOOKUP:
      case DataType.FILE_URL:
      case DataType.USER:
      case DataType.ENUM:
        return maxColWidth + 72
      case DataType.DATE:
      case DataType.DATE_TIME:
      case DataType.FORMULA_DATE:
      case DataType.FORMULA_DATE_TIME:
        return maxColWidth + 88
    }
  }, [cell.column.type, cell.column.innerType, maxColWidth, initialColWidth])

  const isDirty = checkFormikDirtyState(getTableCellInitialValues(cell, value), formikProps.values)

  const isLookupCellEdit =
    cell.column.type === DataType.LOOKUP || cell.column.innerType === DataType.LOOKUP
  const showSaveButtonOnSide =
    cell.column.type === DataType.ARRAY &&
    (cell.column.innerType === DataType.USER || cell.column.innerType === DataType.ENUM)
  const isMobileLookupTableCellEdit = isLookupCellEdit && isMobile

  return (
    <EditableTableCellOriginProvider origin={RecordCellEditOrigin.TABLE_DETAILS}>
      <Prompt
        callback={handleCancel}
        message={t('common.leave_page_warning')}
        shouldConfirm={isDirty}
      />
      <Box
        ref={ref}
        className={cn(
          'flex h-full flex-col overflow-hidden',
          showSaveButtonOnSide && 'flex-row items-center',
        )}
        component="form"
        onSubmit={(e) => e.preventDefault()}
        tabIndex={-1}
      >
        <DialogContent sx={styles.content(columnWidth ?? maxColWidth, isMobileLookupTableCellEdit)}>
          <RecordDetailsCellPermissionsProvider cell={cell} column={cell.column} tableId={tableId}>
            <RecordCellDetails
              addDocumentId={addDocumentId}
              autofocus
              cell={cell}
              columnWidth={columnWidth}
              columns={row.cells.map((cell) => cell.column)}
              lookupGroupId={cell.column.lookupColumnGroup?.id}
              onCancel={handleCancel}
              parentRow={row}
              removeDocumentId={removeDocumentId}
              row={row}
              selectedLookupRowIds={tableCellGroupLookupRowIds}
              showDirtyIndicator={false}
              showSaveButtonOnSide={showSaveButtonOnSide}
              tableId={tableId}
            />
          </RecordDetailsCellPermissionsProvider>
        </DialogContent>
        <DialogActions sx={styles.buttons}>
          <EditTableCellFormButtons
            cellId={cell.id}
            cells={params.row.cells}
            editTableCellFormStates={{
              isLoading: loading,
              setIsLoading,
              isSuccess: success,
              setIsSuccess,
              isFailure: failure,
              setIsFailure,
            }}
            handleCancel={handleCancel}
            isDirty={isDirty}
          />
        </DialogActions>
      </Box>
    </EditableTableCellOriginProvider>
  )
}
